/**
 * AppealSlice
 * 
 * Redux slice for managing appeal state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, AppealSliceState } from './state';

// Import all reducers (renamed to avoid conflicts with exported actions)
import {
  setCurrentAppeal as setCurrentAppealReducer,
  clearAppeal as clearAppealReducer,
  resetAppealState as resetAppealStateReducer,
  updateLetterDraft as updateLetterDraftReducer,
  updateAppealSettings as updateAppealSettingsReducer,
  setIsEditing as setIsEditingReducer,
  addPolicyReference as addPolicyReferenceReducer,
  removePolicyReference as removePolicyReferenceReducer,
  clearPolicyReferences as clearPolicyReferencesReducer,
  setGenerationInProgress as setGenerationInProgressReducer,
  setError as setErrorReducer,
  clearError as clearErrorReducer,
  markAsChanged as markAsChangedReducer,
  markAsSaved as markAsSavedReducer,
  discardChanges as discardChangesReducer,
} from './reducers';

/**
 * Appeal slice definition
 */
const appealSlice = createSlice({
  name: 'appeal',
  initialState,
  reducers: {
    // Core reducers
    setCurrentAppeal: setCurrentAppealReducer,
    clearAppeal: clearAppealReducer,
    resetAppealState: resetAppealStateReducer,

    // Letter draft reducers
    updateLetterDraft: updateLetterDraftReducer,

    // Settings reducers
    updateAppealSettings: updateAppealSettingsReducer,
    setIsEditing: setIsEditingReducer,

    // Policy reference reducers
    addPolicyReference: addPolicyReferenceReducer,
    removePolicyReference: removePolicyReferenceReducer,
    clearPolicyReferences: clearPolicyReferencesReducer,

    // Generation state reducers
    setGenerationInProgress: setGenerationInProgressReducer,
    setError: setErrorReducer,
    clearError: clearErrorReducer,

    // Edit tracking reducers
    markAsChanged: markAsChangedReducer,
    markAsSaved: markAsSavedReducer,
    discardChanges: discardChangesReducer,
  },
});

// Export actions
export const appealActions = appealSlice.actions;

// Export individual actions with Action suffix for convenience
export const {
  setCurrentAppeal: setCurrentAppealAction,
  clearAppeal: clearAppealAction,
  resetAppealState: resetAppealStateAction,
  updateLetterDraft: updateLetterDraftAction,
  updateAppealSettings: updateAppealSettingsAction,
  setIsEditing: setIsEditingAction,
  addPolicyReference: addPolicyReferenceAction,
  removePolicyReference: removePolicyReferenceAction,
  clearPolicyReferences: clearPolicyReferencesAction,
  setGenerationInProgress: setGenerationInProgressAction,
  setError: setErrorAction,
  clearError: clearErrorAction,
  markAsChanged: markAsChangedAction,
  markAsSaved: markAsSavedAction,
  discardChanges: discardChangesAction,
} = appealSlice.actions;

// Also export with original names for backward compatibility
export const {
  setCurrentAppeal,
  clearAppeal,
  resetAppealState,
  updateLetterDraft,
  updateAppealSettings,
  setIsEditing,
  addPolicyReference,
  removePolicyReference,
  clearPolicyReferences,
  setGenerationInProgress,
  setError,
  clearError,
  markAsChanged,
  markAsSaved,
  discardChanges,
} = appealSlice.actions;

// Export reducer
export default appealSlice.reducer;

// Re-export state types
export type { AppealSliceState } from './state';

// Re-export selectors
export * from './selectors';
